// TimesheetDetail.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  TextField,
  Card,
  CardContent,
  styled,
  keyframes,
  Grid,
} from '@mui/material';
import {
  format,
  parse,
  isValid,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isAfter,
  isToday,
} from 'date-fns';
import {
  AccessTimeFilled,
  Place,
  TaskAlt,
  Schedule,
  EventBusy,
  Timelapse,
  ErrorOutline,
  Logout,
} from '@mui/icons-material';
import * as XLSX from 'xlsx-js-style';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import DayDetailDialog from '../../Components/DayDetailDialog';
import { cellInfo } from '../../utils/timesheetUtils';
import Layout from '../../Components/Layout/Layout'; // Import Layout

// Custom theme
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#ff4081' },
    background: { default: '#f5f7fa', paper: '#ffffff' },
    text: { primary: '#1a1a1a', secondary: '#666666' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 600, fontSize: '1.5rem' },
    body2: { fontSize: '0.9rem', color: '#666666' },
    caption: { fontSize: '0.8rem', color: '#999999' },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      },
    },
  },
});

// Animation for fade-in
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled components
const CalendarCell = styled(Paper)(({ theme, bgcolor }) => ({
  padding: theme.spacing(1),
  height: 90,
  backgroundColor: bgcolor,
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  animation: `${fadeIn} 0.5s ease-out`,
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f9f9f9 100%)',
  borderRadius: 12,
  padding: theme.spacing(2),
}));

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ---------- helpers ---------------------------------------------------- */
function fmtTime(ts) {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Dubai',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date(ts * 1000));
}

// Color for Excel cells
function cellFillRgb(hrs, late, oz, date) {
  if (isAfter(date, new Date())) return 'FFE0E0E0'; // Gray - future
  if (hrs === 0) return 'FFFFE6E6'; // Red - no records
  if (oz) return 'FFFFCDD2'; // Dark red - out of zone
  if (late) return 'FFFFF9C4'; // Yellow - late
  return 'FFE8F5E9'; // Green - OK
}

/* ---------- component -------------------------------------------------- */
function TimesheetDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  /* ---------- state ---------------------------------------------------- */
  const [fromDate, setFromDate] = useState(() => startOfMonth(new Date()));
  const [toDate, setToDate] = useState(() => endOfMonth(new Date()));
  const [fromStr, setFromStr] = useState(format(fromDate, 'yyyy-MM-dd'));
  const [toStr, setToStr] = useState(format(toDate, 'yyyy-MM-dd'));

  const [data, setData] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(false);
  const [dayDetail, setDayDetail] = useState(null);

  /* ---------- fetch ---------------------------------------------------- */
  const fetchTimesheet = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    try {
      const s = format(fromDate, 'yyyy-MM-dd');
      const e = format(toDate, 'yyyy-MM-dd');
      const token = encodeURIComponent(localStorage.getItem('auth_token') || '');
      const url =
        `${API_URL}/admin/timesheet_detail?auth_token=${token}` +
        `&employee_id=${id}&start_date=${s}&filter_end_date=${e}`;

      const res = await fetch(url);
      const j = await res.json();
      if (j.status === 'success') {
        setData(j.timesheet);
        setEmployee(j.timesheet.employee);
      } else {
        setError(j.message || 'Server error');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [id, fromDate, toDate]);

  useEffect(() => {
    fetchTimesheet();
  }, [fetchTimesheet]);

  /* ---------- calendar helpers ---------------------------------------- */
  const daysArray = useMemo(() => {
    if (!fromDate || !toDate) return [];
    let cur = startOfWeek(fromDate, { weekStartsOn: 1 });
    const end = endOfWeek(toDate, { weekStartsOn: 1 });
    const arr = [];
    while (cur <= end) {
      arr.push(new Date(cur));
      cur = addDays(cur, 1);
    }
    return arr;
  }, [fromDate, toDate]);

  const daysMap = useMemo(() => {
    if (!data?.days) return {};
    return data.days.reduce((m, d) => {
      m[d.date] = d;
      return m;
    }, {});
  }, [data]);

  const vacations = data?.vacations ?? [];

  /* ---------- statistics ---------------------------------------------- */
  const totalHours = useMemo(
    () => data?.days?.reduce((s, d) => {
      const inf = cellInfo({ days: [d], ...data, zones: data?.allowed_locations || [] }, new Date(d.date));
      return s + (inf.hrs || 0);
    }, 0) || 0,
    [data]
  );
  const totalIns = useMemo(
    () => data?.days?.reduce(
      (s, d) => s + d.records.filter(r => r.event_type === 'clock_in').length, 0) || 0,
    [data]
  );
  const totalLate = useMemo(
    () => data?.days?.reduce(
      (s, d) => s + (cellInfo({ days: [d], ...data, zones: data?.allowed_locations || [] }, new Date(d.date)).late ? 1 : 0), 0) || 0,
    [data]
  );
  const latePct = totalIns ? Math.round((totalLate / totalIns) * 100) : 0;

  const totalEarly = useMemo(
    () => data?.days?.reduce(
      (s, d) => s + (cellInfo({ days: [d], ...data, zones: data?.allowed_locations || [] }, new Date(d.date)).earlyLeave ? 1 : 0), 0) || 0,
    [data]
  );

  const missingDays = useMemo(
    () => daysArray.filter((d) => {
      const recs = daysMap[format(d, 'yyyy-MM-dd')]?.records || [];
      return d.getDay() !== 0 && recs.every(r => r.event_type !== 'clock_in');
    }).length,
    [daysArray, daysMap]
  );

  /* ---------- chart data ---------------------------------------------- */
  const chartData = useMemo(
    () => daysArray.map((d) => {
      const ds = format(d, 'yyyy-MM-dd');
      const inf = cellInfo({ days: daysMap[ds] ? [daysMap[ds]] : [], ...data, zones: data?.allowed_locations || [] }, d);
      return { date: format(d, 'd'), hours: +(inf.hrs || 0).toFixed(2) };
    }),
    [daysArray, daysMap, data]
  );

  /* ---------- Excel export -------------------------------------------- */
  const exportExcel = () => {
    if (!data) return;

    // Headers
    const headerDates = daysArray.map((d) => format(d, 'd'));
    const headerDays = daysArray.map((d) => format(d, 'EE'));
    const aoa = [
      ['ID', 'Name', ...headerDates],
      ['', '', ...headerDays],
    ];

    // Employee row
    const row = [
      employee.id,
      `${employee.first_name} ${employee.last_name}`,
    ];

    daysArray.forEach((d) => {
      const iso = format(d, 'yyyy-MM-dd');
      const vac = vacations.find(v => iso >= v.start_date && iso <= v.end_date);

      if (vac) {
        row.push(vac.reason);
      } else {
        const recs = daysMap[iso]?.records || [];
        const inf = cellInfo({ days: [{ date: iso, records: recs }], ...data, zones: data?.allowed_locations || [] }, d);
        let cellValue = inf.hrsFormatted || '';
        const statuses = [];
        if (inf.late) statuses.push('⏰');
        if (inf.outOfZone) statuses.push('📍');
        if (inf.missedOut) statuses.push('⚠️');
        if (inf.earlyLeave) statuses.push('❌');
        if (statuses.length > 0) cellValue += ` ${statuses.join(' ')}`;
        row.push(cellValue);
      }
    });
    aoa.push(row);

    // Create sheet and styles
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    daysArray.forEach((d, idx) => {
      const col = 2 + idx;
      const addr = XLSX.utils.encode_cell({ r: 2, c: col });
      if (!ws[addr]) ws[addr] = { t: 's', v: '' };

      const iso = format(d, 'yyyy-MM-dd');
      const vac = vacations.find(v => iso >= v.start_date && iso <= v.end_date);
      const recs = daysMap[iso]?.records || [];
      const inf = cellInfo({ days: [{ date: iso, records: recs }], ...data, zones: data?.allowed_locations || [] }, d);

      const rgb = vac ? 'FFBBDEFB' : cellFillRgb(inf.hrs, inf.late, inf.outOfZone, d);

      ws[addr].s = {
        fill: { fgColor: { rgb } },
        font: { sz: 10 },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      };
    });

    ws['!cols'] = [{ wch: 6 }, { wch: 24 }, ...daysArray.map(() => ({ wch: 10 }))];
    ws['!freeze'] = { xSplit: 2, ySplit: 2 };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Timesheet');
    XLSX.writeFile(
      wb,
      `Timesheet_${employee.id}_${format(fromDate, 'yyyyMMdd')}-${format(toDate, 'yyyyMMdd')}.xlsx`
    );
  };

  /* ---------- render --------------------------------------------------- */
  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          ← Back
        </Button>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Layout>
        <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
          <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              {employee && (
                <Typography variant="h5" color="text.primary">
                  Timesheet – {employee.first_name} {employee.last_name}
                </Typography>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/admin')}
                sx={{ borderRadius: 8 }}
              >
                ← Back
              </Button>
            </Box>

            {/* Date range */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
              <TextField
                label="From"
                type="date"
                value={fromStr}
                onChange={(e) => {
                  setFromStr(e.target.value);
                  const d = parse(e.target.value, 'yyyy-MM-dd', new Date());
                  if (isValid(d)) setFromDate(d);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
              />
              <TextField
                label="To"
                type="date"
                value={toStr}
                onChange={(e) => {
                  setToStr(e.target.value);
                  const d = parse(e.target.value, 'yyyy-MM-dd', new Date());
                  if (isValid(d)) setToDate(d);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ bgcolor: 'background.paper', borderRadius: 1 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={exportExcel}
                sx={{ height: 40 }}
              >
                Export to Excel
              </Button>
            </Box>

            {/* Statistics */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {[
                { title: 'Total Hours', value: `${Math.floor(totalHours)}h ${Math.round((totalHours % 1) * 60)}m`, icon: <Timelapse /> },
                { title: 'Clock-ins', value: totalIns, icon: <Schedule /> },
                { title: 'Late', value: totalLate, icon: <AccessTimeFilled /> },
                { title: 'Late %', value: `${latePct}%`, icon: <AccessTimeFilled /> },
                { title: 'Early Leaves', value: totalEarly, icon: <Logout /> },
                { title: 'Missing Days', value: missingDays, icon: <EventBusy /> },
              ].map(({ title, value, icon }) => (
                <Grid key={title} item xs={12} sm={6} md={2.4}>
                  <StatCard>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ color: 'primary.main' }}>{icon}</Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">{title}</Typography>
                        <Typography variant="h6" color="text.primary">{value}</Typography>
                      </Box>
                    </CardContent>
                  </StatCard>
                </Grid>
              ))}
            </Grid>

            {/* Chart */}
            <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
              <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
                Hours Worked
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="date" stroke="#666666" />
                    <YAxis stroke="#666666" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#ffffff',
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Bar
                      dataKey="hours"
                      fill="url(#colorGradient)"
                      radius={[4, 4, 0, 0]}
                      animationDuration={800}
                    />
                    <defs>
                      <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1976d2" />
                        <stop offset="100%" stopColor="#42a5f5" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>

            {/* Calendar header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1, mb: 2 }}>
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dn) => (
                <Typography
                  key={dn}
                  align="center"
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  {dn}
                </Typography>
              ))}
            </Box>

            {/* Calendar grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 1 }}>
              {daysArray.map((d) => {
                const ds = format(d, 'yyyy-MM-dd');
                const recs = daysMap[ds]?.records || [];
                const inf = cellInfo({ days: [{ date: ds, records: recs }], ...data, zones: data?.allowed_locations || [] }, d);
                const vac = vacations.find(v => ds >= v.start_date && ds <= v.end_date);

                let bg = '#ffffff';
                if (vac) bg = '#bbdefb';
                else if (ds === format(new Date(), 'yyyy-MM-dd')) bg = '#fff9c4';
                else if (isAfter(d, new Date())) bg = '#e0e0e0';
                else if (inf.hrs > 0) bg = '#e8f5e9';
                else if (d.getDay() !== 0) bg = '#ffe6e6';

                const icons = [];
                if (inf.late) icons.push(<AccessTimeFilled key="late" fontSize="small" sx={{ color: '#fbc02d' }} />);
                if (inf.outOfZone) icons.push(<Place key="zone" fontSize="small" sx={{ color: '#d32f2f' }} />);
                if (!isToday(d)) {
                  if (inf.missedOut) icons.push(<ErrorOutline key="miss" fontSize="small" sx={{ color: '#ff5722' }} />);
                  if (inf.earlyLeave) icons.push(<Logout key="early" fontSize="small" sx={{ color: '#ff5722' }} />);
                }
                if (icons.length === 0 && inf.hrs > 0) icons.push(<TaskAlt key="ok" fontSize="small" sx={{ color: '#388e3c' }} />);

                return (
                  <CalendarCell
                    key={ds}
                    bgcolor={bg}
                    onClick={() => {
                      setDayDetail({
                        employee: employee,
                        day: { date: ds, records: recs },
                        zones: data?.allowed_locations || [],
                        startTime: inf.startTime || '09:30',
                      });
                      setOpen(true);
                    }}
                  >
                    <Typography align="right" variant="caption">{format(d, 'd')}</Typography>
                    {vac ? (
                      <Box sx={{
                        flex: 1, display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 14, fontWeight: 500, color: '#1976d2',
                      }}>
                        {vac.reason}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', fontSize: 14 }}>
                        {inf.hrsFormatted || '—'}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {icons}
                        </Box>
                      </Box>
                    )}
                  </CalendarCell>
                );
              })}
            </Box>

            <DayDetailDialog detail={dayDetail} setDetail={setDayDetail} />
          </Box>
        </Box>
      </Layout>
    </ThemeProvider>
  );
}

export default TimesheetDetail;
